import {
  type HTMLAttributes,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  useMemo,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { EmptyStateV2 } from "#src/commonV2/EmptyState";
import { LoadingStateV2 } from "#src/commonV2/LoadingState";
import { TableHeaderV2 } from "../../components/TableHeader";
import { TableDndRow } from "./TableDndRow";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

// TODO: maybe change these to refs.
const TABLE_WRAPPER_ID = "table-wrapper";
const TABLE_PAGINATION_ID = "table-pagination";

interface ITableDndV2Props<DataType> {
  data: DataType[];
  getRowKey: (dataItem: DataType) => string;
  isLoading?: boolean;
  columns: {
    title: ReactNode;
    /** Theoretically we should let developers overwrite everything, but I've yet to find any cases we need more than this alignment. */
    titleAlign?: "left" | "center" | "right";
    render: (dataItem: DataType, index: number) => ReactNode;
    /** Literally let you overwrite any props of cell wrapper. */
    overwriteWrapperProps?: HTMLAttributes<HTMLDivElement>;
    gridTemplateValue?: string;
  }[];
  rowSelection?: {
    selectedRowKeys: string[];
    onChange: (newSelectedKeys: string[]) => void;
  };
  options?: {
    fitPageHeight?: boolean;
    maxHeight?: string;
    emptyState?: {
      title: string;
      supportingText?: string;
      type?: "featured-icon" | "illustration";
      buttons?: ReactNode;
    };
  };
  onClickRow?: (item: DataType) => void;
  onDragEnd: (draggingItem: DataType, endItem: DataType | null) => void;
  disabledDnd?: boolean;
}

/** Duh, it's a data table. Aside common things, there's some options to customize more things:
 * @param fitPageHeight (optional) fit the table to the screen. Will add a max-height calculated from the table's position.
 * @param emptyState (optional) config how the empty state will be shown. Same props as EmptyState's props.
 */
export const TableDndV2 = <DataType,>({
  data,
  getRowKey,
  isLoading,
  columns,
  rowSelection,
  options,
  onClickRow,
  onDragEnd,
  disabledDnd = false,
}: ITableDndV2Props<DataType>) => {
  const { t } = useTranslation("common", { keyPrefix: "v2.table" });

  const tableBodyRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // ----------- STATES ----------

  const [optimisticData, setOptimisticData] = useState<DataType[]>(data);
  // Calculate how much height should we left. Table height will be calculated by ()
  const [preferredBufferHeight, setPreferredBufferHeight] = useState<number>();
  const [draggingItem, setDraggingItem] = useState<DataType | null>(null);

  // ---------- COMPUTED ----------

  const tableMaxHeight = useMemo(() => {
    // As noted above: table height is recalculated to adjust to the screen height. `2rem` is added so that it has some spaces below.
    if (options?.fitPageHeight) {
      return preferredBufferHeight
        ? `calc(100vh - ${preferredBufferHeight}px - 2rem)`
        : // Fallback value if the useLayoutEffect bugs out
          "65vh";
    }

    // If not fitPageHeight, then we use the maxHeight option.
    return options?.maxHeight ? options.maxHeight : undefined;
  }, [options, preferredBufferHeight]);

  // ---------- METHODS ----------

  function handleDragStart(event: DragStartEvent) {
    const { active } = event;

    if (active) {
      setDraggingItem(
        optimisticData.find((item) => getRowKey(item) === active.id) ?? null
      );
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active.id !== over?.id) {
      // setReordering(true);
      if (active && over) {
        // Position of active item & over item in the original data list
        const activeIndex = optimisticData.findIndex(
          (item) => getRowKey(item) === active.id
        );
        const overIndex = optimisticData.findIndex(
          (item) => getRowKey(item) === over.id
        );

        // Based on the position, get items that will be swapped.
        const draggedItem = optimisticData[activeIndex];
        // We send the item that is right above the dragged one after dragging is completed
        const swapItem =
          activeIndex < overIndex
            ? optimisticData[overIndex]
            : optimisticData[overIndex - 1];

        setOptimisticData((items) => {
          return arrayMove(items, activeIndex, overIndex);
        });

        onDragEnd(draggedItem, swapItem);
      }
    }
  }

  // ---------- EFFECTS ----------

  useEffect(() => {
    setOptimisticData(data);
  }, [data]);

  // Set preferredBufferHeight by rendered sizes
  useLayoutEffect(() => {
    const tableWrapperElement = document.getElementById(TABLE_WRAPPER_ID);
    const tableBodyElement = tableBodyRef.current;
    const tablePaginationElement = document.getElementById(TABLE_PAGINATION_ID);

    let totalBuffer: number = 0;

    if (tableWrapperElement) {
      totalBuffer =
        totalBuffer +
        parseInt(getComputedStyle(tableWrapperElement).marginBottom, 10);
    }

    if (tableBodyElement) {
      totalBuffer = totalBuffer + tableBodyElement.getBoundingClientRect().top;
    }

    if (tablePaginationElement) {
      totalBuffer =
        totalBuffer + tablePaginationElement.getBoundingClientRect().height;
    }

    setPreferredBufferHeight(totalBuffer);
    // When data change, we have to recalculate this since these height depends on that data content.
  }, [optimisticData]);

  // ---------- COMPUTED ----------

  const gridTemplateColumns = useMemo(() => {
    return (
      (disabledDnd ? "0 " : "3rem ") +
      // The space after "4rem" is necessary for grid template to work properly.
      (rowSelection ? "4rem " : "") +
      columns
        .map((column) =>
          column.gridTemplateValue ? column.gridTemplateValue : "1fr"
        )
        .join(" ")
    );
  }, [columns, rowSelection, disabledDnd]);

  // ---------- RENDER ----------

  return (
    <div
      id={TABLE_WRAPPER_ID}
      className={`border border-gray-v2-200 shadow-xs rounded-xl flex flex-col mb-2 ${isLoading || optimisticData.length === 0 ? "items-center justify-center flex-1 overflow-hidden" : ""}`}
    >
      {isLoading ? (
        <LoadingStateV2 />
      ) : optimisticData.length === 0 ? (
        <EmptyStateV2
          title={
            options && options.emptyState
              ? options.emptyState.title
              : t("emptyState")
          }
          {...options?.emptyState}
        />
      ) : (
        <>
          <div
            className="grid overflow-x-auto transition-all"
            style={{ gridTemplateColumns: gridTemplateColumns }}
          >
            {/* Table header */}
            <TableHeaderV2
              headerProps={columns.map((column) => ({
                name: column.title,
                align: column.titleAlign ? column.titleAlign : "left",
              }))}
              allSelected={
                rowSelection
                  ? {
                      value: rowSelection
                        ? rowSelection.selectedRowKeys.length > 0
                        : false,
                      onChange: () => {
                        if (rowSelection) {
                          if (rowSelection.selectedRowKeys.length === 0) {
                            rowSelection.onChange(
                              optimisticData.map(getRowKey)
                            );
                          } else {
                            rowSelection.onChange([]);
                          }
                        }
                      },
                      ready: !isLoading && optimisticData.length > 0,
                    }
                  : undefined
              }
              featureColumns={1}
            />
            {/* Table header end */}

            {/* Table body */}
            <div
              ref={tableBodyRef}
              className="grid grid-cols-subgrid col-span-full overflow-x-hidden overflow-y-auto"
              style={{
                maxHeight: tableMaxHeight,
              }}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={optimisticData.map((item) => getRowKey(item))}
                  strategy={verticalListSortingStrategy}
                >
                  {optimisticData.map((item, rowIndex) => (
                    <TableDndRow
                      key={`table-${rowIndex}`}
                      item={item}
                      rowIndex={rowIndex}
                      getRowKey={getRowKey}
                      columns={columns}
                      rowSelection={rowSelection}
                      onClickRow={onClickRow}
                      isLastRow={rowIndex === optimisticData.length - 1}
                    />
                  ))}
                </SortableContext>
                <DragOverlay modifiers={[restrictToVerticalAxis]}>
                  <div
                    className="grid border-t border-gray-v2-200"
                    style={{ gridTemplateColumns: gridTemplateColumns }}
                  >
                    <TableDndRow
                      item={draggingItem}
                      rowIndex={
                        optimisticData.findIndex(
                          (item) =>
                            draggingItem &&
                            getRowKey(item) === getRowKey(draggingItem)
                        ) ?? -1
                      }
                      getRowKey={getRowKey}
                      columns={columns}
                      rowSelection={rowSelection}
                      onClickRow={onClickRow}
                    />
                  </div>
                </DragOverlay>
              </DndContext>
            </div>
            {/* Table body end */}
          </div>
        </>
      )}
    </div>
  );
};
